"use client";

import { useState, useEffect } from "react";
import dynamic from "next/dynamic";

/**
 * Lazy-loaded chat widget.
 *
 * The @assistant-ui + ai-sdk libs are ~230 KiB and were loading on every
 * page even though the modal was hidden until clicked. Now we render only
 * the floating "Chat" button, then dynamic-import the heavy runtime on
 * first user interaction (button click, or any pointer move after a 6s
 * idle window so it's warm if the user is reading).
 *
 * Once mounted, the real modal handles everything; this wrapper is just
 * the deferred-load shim.
 */

const ChatMount = dynamic(() => import("./ChatMount"), {
  ssr: false,
  loading: () => null,
});

export default function LazyChat() {
  const [mounted, setMounted] = useState(false);

  // Warm-mount on first user gesture after the page has been idle a bit,
  // so the heavy chunk has time to download before the user actually clicks.
  useEffect(() => {
    if (mounted) return;
    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setMounted(true);
    };
    const warmTimer = setTimeout(trigger, 6_000);
    const opts = { once: true, passive: true } as AddEventListenerOptions;
    window.addEventListener("pointerdown", trigger, opts);
    window.addEventListener("keydown", trigger, opts);
    window.addEventListener("scroll", trigger, opts);
    return () => {
      clearTimeout(warmTimer);
      window.removeEventListener("pointerdown", trigger);
      window.removeEventListener("keydown", trigger);
      window.removeEventListener("scroll", trigger);
    };
  }, [mounted]);

  if (!mounted) return null;
  return <ChatMount />;
}
